import React, { useState, useCallback, useEffect } from 'react';
import SalesCompletenessPanel from '../components/SalesCompletenessPanel';
import { useNavigate } from 'react-router-dom';
import { accountsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Upload, FileSpreadsheet, Columns3, Eye, Cpu, ClipboardCheck, Zap, ChevronRight, Check, X, AlertTriangle, ArrowLeft, Search, RefreshCw, Loader2, Shield, ArrowRightLeft, CreditCard, Banknote, Smartphone, Store, Clock, Truck } from 'lucide-react';
import { DeletionRequestModal, ActiveDeleteRequestBadge } from '../components/DeletionRequestModal';
import PeriodToolbar from '../components/PeriodToolbar';
import { usePeriod } from '../hooks/usePeriod';
import { PERIOD_MODES } from '../lib/period';

// Shared color helpers
const scoreColor = (s, type = 'text') => {
  const prefix = type === 'bg' ? 'bg' : 'text';
  if (s >= 75) return `${prefix}-emerald-400`;
  if (s >= 55) return `${prefix}-amber-400`;
  return `${prefix}-red-400`;
};

// ── Stage 3: Legacy upload components REMOVED ──
// Upload workflow UI (Steps 1-7, Transaction upload) permanently removed.
// Historical upload data remains accessible via UploadHistory (read-only audit).


// ═══════════════════════════════════════════════════════════════
// UNIFIED UPLOAD HISTORY
// ═══════════════════════════════════════════════════════════════
function UploadHistory({ uploads, loading, onRefresh }) {
  if (loading) return <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 text-zinc-400 animate-spin" /></div>;

  return (
    <Card className="bg-zinc-900/40 border-zinc-800/40">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm text-zinc-300">Upload History</CardTitle>
          <Button variant="ghost" size="sm" onClick={onRefresh} className="text-zinc-500 hover:text-zinc-300 h-7 w-7 p-0" data-testid="refresh-history-btn">
            <RefreshCw className="w-3.5 h-3.5" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {(!uploads || uploads.length === 0) ? (
          <p className="text-center text-zinc-600 py-6 text-sm">No uploads yet</p>
        ) : (
          <div className="space-y-2">
            {uploads.map(u => {
              const isTxn = u.upload_type === 'SALE_TRANSACTIONS';
              const statusColor = u.status === 'ACTIVATED' || u.status === 'TXN_ACTIVATED'
                ? 'text-emerald-400 border-emerald-500/30'
                : u.status === 'FAILED' ? 'text-red-400 border-red-500/30'
                : 'text-amber-400 border-amber-500/30';
              return (
                <div key={u.upload_id} className="flex items-center justify-between p-3 rounded-lg bg-zinc-800/30 border border-zinc-800/50"
                  data-testid={`history-${u.upload_id?.slice(0, 8)}`}>
                  <div className="flex items-center gap-3">
                    {isTxn ? <ArrowRightLeft className="w-4 h-4 text-amber-400 flex-shrink-0" />
                           : <FileSpreadsheet className="w-4 h-4 text-emerald-400 flex-shrink-0" />}
                    <div>
                      <p className="text-xs text-zinc-200 font-medium truncate max-w-[200px]">{u.file_name}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <Badge variant="outline" className={`text-[8px] ${isTxn ? 'border-amber-500/30 text-amber-400' : 'border-emerald-500/30 text-emerald-400'}`}>
                          {u.upload_type || 'SALES_REPORT'}
                        </Badge>
                        <span className="text-[10px] text-zinc-500">
                          {u.total_rows || u.valid_rows || 0} rows
                        </span>
                        {u.date_from && <span className="text-[10px] text-zinc-600">{u.date_from}</span>}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <Badge variant="outline" className={`text-[9px] ${statusColor}`}>{u.status}</Badge>
                    <p className="text-[9px] text-zinc-600 mt-0.5">
                      {u.created_at ? new Date(u.created_at).toLocaleDateString('en-IN') : ''}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Main Page
export default function SalesCapturePage() {
  const [view, setView] = useState('status'); // 'status', 'comparison', 'operations'
  const [opsData, setOpsData] = useState(null);
  const [loadingOps, setLoadingOps] = useState(false);
  // Sales Source status
  const [sourceStatus, setSourceStatus] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [captureSummary, setCaptureSummary] = useState(null);
  const [captureTaxes, setCaptureTaxes] = useState(null);
  const [captureHealth, setCaptureHealth] = useState(null);
  // Comparison
  const [comparison, setComparison] = useState(null);
  const [loadingComparison, setLoadingComparison] = useState(false);
  const periodState = usePeriod(PERIOD_MODES.RANGE);
  // Migration
  const [migrationData, setMigrationData] = useState(null);
  const [loadingMigration, setLoadingMigration] = useState(false);
  const [runningCheck, setRunningCheck] = useState(false);
  const navigate = useNavigate();

  const loadSourceStatus = useCallback(async () => {
    setLoadingStatus(true);
    try {
      const [statusRes, summaryRes, taxesRes] = await Promise.allSettled([
        accountsAPI.getSalesCaptureSourceStatus(),
        accountsAPI.getSalesCaptureSummary({ date_from: '2026-01-01', date_to: '2026-12-31' }),
        accountsAPI.getSalesCaptureTaxes({ date_from: '2026-01-01', date_to: '2026-12-31' }),
      ]);
      if (statusRes.status === 'fulfilled') setSourceStatus(statusRes.value.data);
      if (summaryRes.status === 'fulfilled') setCaptureSummary(summaryRes.value.data);
      if (taxesRes.status === 'fulfilled') setCaptureTaxes(taxesRes.value.data);
      if (statusRes.status === 'fulfilled') setCaptureHealth(statusRes.value.data?.health);
    } catch {} finally { setLoadingStatus(false); }
  }, []);

  const loadComparison = useCallback(async () => {
    setLoadingComparison(true);
    try {
      const res = await accountsAPI.getSalesCaptureComparison(periodState.apiParams);
      setComparison(res.data);
    } catch {} finally { setLoadingComparison(false); }
  }, [periodState.apiParams]);

  const loadMigrationStatus = useCallback(async () => {
    setLoadingMigration(true);
    try {
      const res = await accountsAPI.getMigrationStatus();
      setMigrationData(res.data);
    } catch {} finally { setLoadingMigration(false); }
  }, []);

  const loadOperationsSummary = useCallback(async () => {
    setLoadingOps(true);
    try {
      const res = await accountsAPI.getOperationsSummary();
      setOpsData(res.data);
    } catch (err) { console.error('Failed to load operations summary:', err); }
    finally { setLoadingOps(false); }
  }, []);

  const triggerHealthCheck = async () => {
    setRunningCheck(true);
    try {
      await accountsAPI.runMigrationHealthCheck();
      await loadMigrationStatus();
    } catch {} finally { setRunningCheck(false); }
  };

  useEffect(() => {
    if (view === 'status') { loadSourceStatus(); loadMigrationStatus(); }
    if (view === 'comparison') loadComparison();
    if (view === 'operations') loadOperationsSummary();
  }, [view, loadSourceStatus, loadComparison, loadMigrationStatus, loadOperationsSummary]);

  // Auto-refresh every 60s on status tab
  useEffect(() => {
    if (view !== 'status') return;
    const interval = setInterval(loadSourceStatus, 60000);
    return () => clearInterval(interval);
  }, [view, loadSourceStatus]);

  const migrationStatus = sourceStatus?.migration_status || 'PENDING';
  const healthStatus = captureHealth?.status || 'unknown';
  const syncInfo = sourceStatus?.sync || {};
  const isStale = syncInfo.is_stale;
  const freshnessMin = syncInfo.freshness_minutes;

  const fmtCurrency = (v) => (v || 0).toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 });
  const varianceColor = (level) => level === 'GREEN' ? 'text-emerald-400' : level === 'AMBER' ? 'text-amber-400' : 'text-red-400';
  const varianceBg = (level) => level === 'GREEN' ? 'border-emerald-500/30 bg-emerald-500/5' : level === 'AMBER' ? 'border-amber-500/30 bg-amber-500/5' : 'border-red-500/30 bg-red-500/5';

  return (
    <div className="min-h-screen bg-zinc-950 p-4 sm:p-6 max-w-4xl mx-auto" data-testid="sales-capture-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/pnl')} className="text-zinc-500 hover:text-zinc-300">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div>
            <h1 className="text-lg font-semibold text-zinc-100" data-testid="page-title">Sales Source</h1>
            <p className="text-xs text-zinc-500">
              Sales data is now managed through the Sales Capture Engine
            </p>
          </div>
        </div>
        <div className="flex gap-1.5 flex-wrap">
          <Button variant={view === 'status' ? 'default' : 'outline'} size="sm"
            className={view === 'status' ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'border-zinc-700 text-zinc-400'}
            onClick={() => setView('status')} data-testid="tab-source-status">
            Source Status
          </Button>
          <Button variant={view === 'comparison' ? 'default' : 'outline'} size="sm"
            className={view === 'comparison' ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'border-zinc-700 text-zinc-400'}
            onClick={() => setView('comparison')} data-testid="tab-comparison">
            Comparison
          </Button>
          <Button variant={view === 'operations' ? 'default' : 'outline'} size="sm"
            className={view === 'operations' ? 'bg-amber-600 hover:bg-amber-700 text-white' : 'border-zinc-700 text-zinc-400'}
            onClick={() => setView('operations')} data-testid="tab-operations">
            Migration Ops
          </Button>
        </div>
      </div>

      {/* ─── Sales Source Status View ─── */}
      {view === 'status' && (
        <div className="space-y-4" data-testid="source-status-view">
          {/* Stale warning */}
          {isStale && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 flex items-start gap-3" data-testid="stale-warning">
              <AlertTriangle className="w-5 h-5 text-red-400 mt-0.5 shrink-0" />
              <div className="text-xs text-red-200/80">
                <p className="font-semibold text-red-300">Data is stale</p>
                Last sync was {freshnessMin ? `${Math.round(freshnessMin)} minutes` : 'never'} ago (threshold: {syncInfo.stale_threshold_minutes}min).
              </div>
            </div>
          )}

          {/* Source Health + Sync Status */}
          <Card className="bg-zinc-900/60 border-zinc-800">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm text-zinc-300 flex items-center gap-2">
                  <Store className="w-4 h-4 text-emerald-400" /> Sales Capture Engine
                </CardTitle>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className={`text-[9px] ${
                    healthStatus === 'healthy' ? 'border-emerald-500/40 text-emerald-400' :
                    healthStatus === 'UNREACHABLE' ? 'border-red-500/40 text-red-400' :
                    'border-zinc-600 text-zinc-400'
                  }`} data-testid="health-badge">
                    {healthStatus === 'healthy' ? 'HEALTHY' : healthStatus.toUpperCase()}
                  </Badge>
                  <Button variant="ghost" size="sm" onClick={loadSourceStatus} disabled={loadingStatus}
                    className="h-7 px-2 text-zinc-500 hover:text-zinc-300" data-testid="refresh-status-btn">
                    <RefreshCw className={`w-3.5 h-3.5 ${loadingStatus ? 'animate-spin' : ''}`} />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="bg-zinc-800/50 rounded-lg p-3">
                  <p className="text-[10px] text-zinc-500 uppercase tracking-wide">Source</p>
                  <p className="text-sm font-semibold text-emerald-400 mt-0.5" data-testid="source-label">Sales Capture</p>
                </div>
                <div className="bg-zinc-800/50 rounded-lg p-3">
                  <p className="text-[10px] text-zinc-500 uppercase tracking-wide">Migration</p>
                  <p className={`text-sm font-semibold mt-0.5 ${migrationStatus === 'ACTIVE' ? 'text-emerald-400' : 'text-amber-400'}`} data-testid="migration-status">
                    {migrationStatus}
                  </p>
                </div>
                <div className="bg-zinc-800/50 rounded-lg p-3">
                  <p className="text-[10px] text-zinc-500 uppercase tracking-wide">Capture Txns</p>
                  <p className="text-sm font-semibold text-zinc-200 mt-0.5" data-testid="capture-count">
                    {sourceStatus?.local_data?.capture_transactions ?? '—'}
                  </p>
                </div>
                <div className="bg-zinc-800/50 rounded-lg p-3">
                  <p className="text-[10px] text-zinc-500 uppercase tracking-wide">Legacy Txns</p>
                  <p className="text-sm font-semibold text-zinc-400 mt-0.5" data-testid="legacy-count">
                    {sourceStatus?.local_data?.legacy_transactions ?? '—'}
                  </p>
                </div>
              </div>

              {/* Live Sync Status */}
              <div className="bg-zinc-800/40 rounded-lg p-3 border border-zinc-700/50">
                <p className="text-[10px] text-zinc-500 uppercase tracking-wide mb-2">Sync Status</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                  <div>
                    <p className="text-zinc-500">Last Successful Sync</p>
                    <p className="text-zinc-200 font-medium mt-0.5" data-testid="last-sync-time">
                      {syncInfo.last_successful_sync ? new Date(syncInfo.last_successful_sync).toLocaleString() : 'Never'}
                    </p>
                  </div>
                  <div>
                    <p className="text-zinc-500">Data Freshness</p>
                    <p className={`font-medium mt-0.5 ${isStale ? 'text-red-400' : freshnessMin != null ? 'text-emerald-400' : 'text-zinc-400'}`} data-testid="freshness">
                      {freshnessMin != null ? (freshnessMin < 60 ? `${Math.round(freshnessMin)}m ago` : `${Math.round(freshnessMin / 60)}h ago`) : 'No data'}
                    </p>
                  </div>
                  <div>
                    <p className="text-zinc-500">Latest Data Date</p>
                    <p className="text-zinc-200 font-medium mt-0.5" data-testid="latest-data-date">
                      {syncInfo.latest_data_date || '—'}
                    </p>
                  </div>
                  <div>
                    <p className="text-zinc-500">Auto-refresh</p>
                    <p className="text-emerald-400 font-medium mt-0.5 flex items-center gap-1">
                      <Clock className="w-3 h-3" /> 60s
                    </p>
                  </div>
                </div>
              </div>

              {/* Confidence */}
              {sourceStatus?.confidence && (
                <div className="bg-zinc-800/40 rounded-lg p-3 border border-zinc-700/50">
                  <p className="text-[10px] text-zinc-500 uppercase tracking-wide mb-2">Data Confidence</p>
                  <div className="flex flex-wrap items-center gap-4 text-xs">
                    <span className="text-zinc-400">Avg Score: <span className={scoreColor(sourceStatus.confidence.avg_score || 0)} data-testid="avg-confidence">
                      {sourceStatus.confidence.avg_score || 0}%
                    </span></span>
                    <span className="text-zinc-400">Threshold: <span className="text-zinc-300">{sourceStatus.confidence.threshold}%</span></span>
                    {sourceStatus.confidence.validation_pass_count > 0 && (
                      <span className="text-emerald-400 flex items-center gap-1">
                        <Check className="w-3 h-3" /> {sourceStatus.confidence.validation_pass_count} passed
                      </span>
                    )}
                    {sourceStatus.confidence.low_confidence_count > 0 && (
                      <span className="text-amber-400 flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" /> {sourceStatus.confidence.low_confidence_count} low ({sourceStatus.confidence.low_confidence_pct}%)
                      </span>
                    )}
                    {sourceStatus.confidence.validation_fail_count > 0 && (
                      <span className="text-red-400 flex items-center gap-1">
                        <X className="w-3 h-3" /> {sourceStatus.confidence.validation_fail_count} failed ({sourceStatus.confidence.fail_pct}%)
                      </span>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Sales Summary from Capture */}
          <Card className="bg-zinc-900/60 border-zinc-800">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm text-zinc-300 flex items-center gap-2">
                <Zap className="w-4 h-4 text-amber-400" /> Sales Summary (from Capture)
              </CardTitle>
            </CardHeader>
            <CardContent>
              {captureSummary && captureSummary.total_orders > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="bg-zinc-800/50 rounded-lg p-3">
                    <p className="text-[10px] text-zinc-500 uppercase">Gross Sales</p>
                    <p className="text-sm font-semibold text-zinc-100 mt-0.5" data-testid="capture-gross-sales">{fmtCurrency(captureSummary.gross_sales)}</p>
                  </div>
                  <div className="bg-zinc-800/50 rounded-lg p-3">
                    <p className="text-[10px] text-zinc-500 uppercase">Net Sales</p>
                    <p className="text-sm font-semibold text-emerald-400 mt-0.5" data-testid="capture-net-sales">{fmtCurrency(captureSummary.net_sales)}</p>
                  </div>
                  <div className="bg-zinc-800/50 rounded-lg p-3">
                    <p className="text-[10px] text-zinc-500 uppercase">Total Tax</p>
                    <p className="text-sm font-semibold text-zinc-200 mt-0.5" data-testid="capture-tax">{fmtCurrency(captureSummary.taxes_total)}</p>
                  </div>
                  <div className="bg-zinc-800/50 rounded-lg p-3">
                    <p className="text-[10px] text-zinc-500 uppercase">Orders</p>
                    <p className="text-sm font-semibold text-zinc-200 mt-0.5" data-testid="capture-orders">{captureSummary.total_orders?.toLocaleString()}</p>
                  </div>
                </div>
              ) : (
                <div className="text-center py-6 text-zinc-500 text-xs" data-testid="no-capture-data">
                  <Store className="w-8 h-8 mx-auto mb-2 text-zinc-600" />
                  No sales data available from Sales Capture yet.
                  <br />Upload sales through the Sales Capture Engine to see data here.
                </div>
              )}
            </CardContent>
          </Card>

          {/* Tax Breakdown from Capture */}
          {captureTaxes && captureTaxes.tax_components && captureTaxes.tax_components.length > 0 && (
            <Card className="bg-zinc-900/60 border-zinc-800">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm text-zinc-300 flex items-center gap-2">
                  <CreditCard className="w-4 h-4 text-blue-400" /> Tax Breakdown (from Capture)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {captureTaxes.tax_components.map((comp, i) => (
                    <div key={i} className="flex justify-between items-center bg-zinc-800/50 rounded-lg px-3 py-2">
                      <span className="text-xs text-zinc-300">{comp.type || comp.component}</span>
                      <span className="text-xs font-mono text-zinc-200">{fmtCurrency(comp.amount || comp.tax_amount)}</span>
                    </div>
                  ))}
                  <div className="flex justify-between items-center bg-zinc-800/80 rounded-lg px-3 py-2 border border-zinc-700/50">
                    <span className="text-xs font-semibold text-zinc-200">Total Tax</span>
                    <span className="text-xs font-mono font-semibold text-zinc-100">{fmtCurrency(captureTaxes.total_tax)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Migration Readiness Panel */}
          <Card className="bg-zinc-900/60 border-zinc-800" data-testid="migration-readiness-panel">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm text-zinc-300 flex items-center gap-2">
                  <Shield className="w-4 h-4 text-blue-400" /> Migration Readiness
                </CardTitle>
                <div className="flex items-center gap-2">
                  {migrationData?.config && (
                    <Badge variant="outline" className={`text-[9px] ${
                      migrationData.config.current_stage === 'SHADOW' ? 'border-blue-500/40 text-blue-400' :
                      migrationData.config.current_stage === 'SOFT_CUTOVER' ? 'border-amber-500/40 text-amber-400' :
                      'border-emerald-500/40 text-emerald-400'
                    }`} data-testid="current-stage-badge">
                      {migrationData.config.current_stage}
                    </Badge>
                  )}
                  <Button variant="ghost" size="sm" onClick={triggerHealthCheck} disabled={runningCheck}
                    className="h-7 px-2 text-zinc-500 hover:text-zinc-300" data-testid="run-health-check-btn">
                    {runningCheck ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {/* Readiness Gates */}
              {migrationData?.health_summary?.gates ? (
                <>
                  <div className="grid grid-cols-5 gap-2" data-testid="readiness-gates">
                    {Object.entries(migrationData.health_summary.gates).map(([gate, info]) => (
                      <div key={gate} className={`rounded-lg p-2.5 border ${info.pass ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-red-500/30 bg-red-500/5'}`}
                        data-testid={`gate-${gate}`}>
                        <div className="flex items-center gap-1 mb-1">
                          {info.pass ? <Check className="w-3 h-3 text-emerald-400" /> : <X className="w-3 h-3 text-red-400" />}
                          <span className="text-[9px] text-zinc-500 uppercase tracking-wide">{gate.replace(/_/g, ' ')}</span>
                        </div>
                        <p className={`text-[10px] font-medium ${info.pass ? 'text-emerald-400' : 'text-red-400'}`}>
                          {info.pass ? 'PASS' : 'FAIL'}
                        </p>
                        <p className="text-[9px] text-zinc-500 mt-0.5">{info.value}</p>
                      </div>
                    ))}
                  </div>

                  {/* Overall Status */}
                  <div className={`flex items-center justify-between px-3 py-2 rounded-lg border ${
                    migrationData.health_summary.overall_status === 'READY_FOR_SOFT_CUTOVER' ? 'border-emerald-500/30 bg-emerald-500/5' :
                    migrationData.health_summary.overall_status === 'NOT_READY' ? 'border-red-500/30 bg-red-500/5' :
                    migrationData.health_summary.overall_status === 'ACCUMULATING' ? 'border-blue-500/30 bg-blue-500/5' :
                    'border-amber-500/30 bg-amber-500/5'
                  }`} data-testid="overall-readiness">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-semibold text-zinc-200">
                        {migrationData.health_summary.overall_status === 'READY_FOR_SOFT_CUTOVER' ? 'READY FOR SOFT CUTOVER' :
                         migrationData.health_summary.overall_status === 'READY_FOR_FULL_MIGRATION' ? 'READY FOR FULL MIGRATION' :
                         migrationData.health_summary.overall_status === 'ACCUMULATING' ? 'ACCUMULATING DATA' :
                         migrationData.health_summary.overall_status === 'NOT_READY' ? 'NOT READY' : 'WARNING'}
                      </span>
                      <span className="text-[10px] text-zinc-500">
                        ({migrationData.health_summary.days_checked} days checked, {migrationData.stability_days} stability streak)
                      </span>
                    </div>
                    {migrationData.health_summary.critical_alerts > 0 && (
                      <span className="text-[10px] text-red-400">{migrationData.health_summary.critical_alerts} critical</span>
                    )}
                    {migrationData.health_summary.warning_alerts > 0 && (
                      <span className="text-[10px] text-amber-400">{migrationData.health_summary.warning_alerts} warnings</span>
                    )}
                  </div>

                  {/* Key Metrics */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                    <div className="bg-zinc-800/40 rounded-lg p-2">
                      <p className="text-[9px] text-zinc-500 uppercase">Avg Rev Variance</p>
                      <p className={`font-semibold mt-0.5 ${migrationData.health_summary.avg_revenue_variance_pct <= 3 ? 'text-emerald-400' : 'text-red-400'}`}>
                        {migrationData.health_summary.avg_revenue_variance_pct}%
                      </p>
                    </div>
                    <div className="bg-zinc-800/40 rounded-lg p-2">
                      <p className="text-[9px] text-zinc-500 uppercase">Coverage</p>
                      <p className={`font-semibold mt-0.5 ${migrationData.health_summary.avg_coverage_pct >= 100 ? 'text-emerald-400' : 'text-red-400'}`}>
                        {migrationData.health_summary.avg_coverage_pct}%
                      </p>
                    </div>
                    <div className="bg-zinc-800/40 rounded-lg p-2">
                      <p className="text-[9px] text-zinc-500 uppercase">Missing Days</p>
                      <p className={`font-semibold mt-0.5 ${migrationData.health_summary.total_missing_days === 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                        {migrationData.health_summary.total_missing_days}
                      </p>
                    </div>
                    <div className="bg-zinc-800/40 rounded-lg p-2">
                      <p className="text-[9px] text-zinc-500 uppercase">Fallback Usage</p>
                      <p className={`font-semibold mt-0.5 ${migrationData.health_summary.avg_fallback_usage_pct <= 0 ? 'text-emerald-400' : 'text-amber-400'}`}>
                        {migrationData.health_summary.avg_fallback_usage_pct}%
                      </p>
                      <p className="text-[8px] text-zinc-600 mt-0.5">% of P&L requests using legacy</p>
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-center py-4 text-zinc-500 text-xs" data-testid="no-health-data">
                  <Shield className="w-6 h-6 mx-auto mb-2 text-zinc-600" />
                  No health check data yet. Run a health check to start tracking migration readiness.
                  <Button variant="outline" size="sm" onClick={triggerHealthCheck} disabled={runningCheck}
                    className="mt-3 border-zinc-700 text-zinc-400 hover:text-zinc-200" data-testid="first-health-check-btn">
                    {runningCheck ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
                    Run Health Check
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Architecture notice */}
          <Card className="bg-zinc-900/40 border-zinc-800/60">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <Shield className="w-5 h-5 text-emerald-400 mt-0.5 shrink-0" />
                <div className="text-xs text-zinc-500 leading-relaxed">
                  <p className="text-zinc-300 font-medium mb-1">Accounts Engine is a read-only consumer</p>
                  Sales truth is owned by the Sales Capture Engine. Accounts does not recompute taxes, infer channels,
                  or rebuild payment modes. All financial interpretations (P&L, tax summaries, forecasts) are derived
                  from the canonical Sales Capture data.
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ─── Comparison View ─── */}
      {view === 'comparison' && (
        <div className="space-y-4" data-testid="comparison-view">
          {/* Date Range Selector */}
          <Card className="bg-zinc-900/60 border-zinc-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3 flex-wrap">
                <PeriodToolbar state={periodState} mode={PERIOD_MODES.RANGE} resolved={comparison} />
                <Button size="sm" onClick={loadComparison} disabled={loadingComparison}
                  className="bg-blue-600 hover:bg-blue-700 text-white h-7" data-testid="run-comparison-btn">
                  {loadingComparison ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
                  Compare
                </Button>
                {comparison && (
                  <Badge variant="outline" className={`text-[9px] ${
                    comparison.overall_verdict === 'GREEN' ? 'border-emerald-500/40 text-emerald-400' :
                    comparison.overall_verdict === 'AMBER' ? 'border-amber-500/40 text-amber-400' :
                    'border-red-500/40 text-red-400'
                  }`} data-testid="overall-verdict">
                    {comparison.overall_verdict} ({comparison.red_fields} red, {comparison.amber_fields} amber)
                  </Badge>
                )}
              </div>
            </CardContent>
          </Card>

          <SalesCompletenessPanel dateFrom={periodState.period.from} dateTo={periodState.period.to} />

          {comparison && (
            <>
              {/* Cutover Readiness */}
              <div className={`flex items-start gap-3 px-4 py-3 rounded-lg border ${
                comparison.cutover_readiness?.status === 'READY' ? 'bg-emerald-500/8 border-emerald-500/30' :
                comparison.cutover_readiness?.status === 'BLOCKED' ? 'bg-red-500/8 border-red-500/30' :
                'bg-amber-500/8 border-amber-500/30'
              }`} data-testid="cutover-readiness">
                {comparison.cutover_readiness?.status === 'READY' ?
                  <Check className="w-5 h-5 text-emerald-400 mt-0.5 shrink-0" /> :
                  <AlertTriangle className="w-5 h-5 mt-0.5 shrink-0" style={{color: comparison.cutover_readiness?.status === 'BLOCKED' ? '#f87171' : '#fbbf24'}} />
                }
                <div className="text-xs">
                  <p className={`font-semibold ${comparison.cutover_readiness?.status === 'READY' ? 'text-emerald-300' : comparison.cutover_readiness?.status === 'BLOCKED' ? 'text-red-300' : 'text-amber-300'}`}>
                    Cutover: {comparison.cutover_readiness?.status}
                  </p>
                  <p className="text-zinc-400 mt-0.5">{comparison.cutover_readiness?.message}</p>
                </div>
              </div>

              {/* Variance Reason Summary */}
              {comparison.variance_reasons?.length > 0 && (
                <Card className="bg-zinc-900/60 border-zinc-800">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm text-zinc-300">Variance Reason Summary</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {comparison.variance_reasons.map((r, i) => (
                        <div key={i} className={`flex items-start gap-2 px-3 py-2 rounded-lg border ${
                          r.severity === 'CRITICAL' ? 'border-red-500/40 bg-red-500/5' :
                          r.severity === 'HIGH' ? 'border-red-500/30 bg-red-500/5' :
                          r.severity === 'MEDIUM' ? 'border-amber-500/30 bg-amber-500/5' :
                          'border-zinc-700 bg-zinc-800/30'
                        }`} data-testid={`reason-${r.code}`}>
                          <Badge variant="outline" className={`text-[8px] shrink-0 mt-0.5 ${
                            r.severity === 'CRITICAL' || r.severity === 'HIGH' ? 'border-red-500/40 text-red-400' :
                            r.severity === 'MEDIUM' ? 'border-amber-500/40 text-amber-400' :
                            'border-zinc-600 text-zinc-400'
                          }`}>{r.severity}</Badge>
                          <div>
                            <span className="text-[10px] text-zinc-500 font-mono">{r.code}</span>
                            <p className="text-xs text-zinc-300 mt-0.5">{r.message}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Date Coverage Diagnostics */}
              {comparison.date_coverage && (
                <Card className="bg-zinc-900/60 border-zinc-800">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm text-zinc-300">Date Coverage</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
                      <div className="bg-zinc-800/50 rounded-lg p-2.5">
                        <p className="text-[9px] text-zinc-500 uppercase">Capture Range</p>
                        <p className="text-xs text-blue-400 font-medium mt-0.5">
                          {comparison.date_coverage.capture.first_date || 'None'} — {comparison.date_coverage.capture.last_date || 'None'}
                        </p>
                        <p className="text-[10px] text-zinc-500 mt-0.5">{comparison.date_coverage.capture.days_with_data} days</p>
                      </div>
                      <div className="bg-zinc-800/50 rounded-lg p-2.5">
                        <p className="text-[9px] text-zinc-500 uppercase">Legacy Range</p>
                        <p className="text-xs text-zinc-300 font-medium mt-0.5">
                          {comparison.date_coverage.legacy.first_date || 'None'} — {comparison.date_coverage.legacy.last_date || 'None'}
                        </p>
                        <p className="text-[10px] text-zinc-500 mt-0.5">{comparison.date_coverage.legacy.days_with_data} days</p>
                      </div>
                      <div className="bg-zinc-800/50 rounded-lg p-2.5">
                        <p className="text-[9px] text-zinc-500 uppercase">Overlap</p>
                        <p className={`text-xs font-semibold mt-0.5 ${comparison.date_coverage.overlap_days > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                          {comparison.date_coverage.overlap_days} days
                        </p>
                      </div>
                      <div className="bg-zinc-800/50 rounded-lg p-2.5">
                        <p className="text-[9px] text-zinc-500 uppercase">Legacy-only</p>
                        <p className={`text-xs font-semibold mt-0.5 ${comparison.date_coverage.legacy_only_days > 0 ? 'text-amber-400' : 'text-zinc-400'}`}>
                          {comparison.date_coverage.legacy_only_days} days
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* ═══ LAYER 1: Revenue Comparison (PRIMARY — drives cutover) ═══ */}
              <Card className="bg-zinc-900/60 border-zinc-800 border-l-2 border-l-emerald-500">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-zinc-300 flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-emerald-500" /> Revenue Comparison
                    <Badge variant="outline" className="text-[8px] border-emerald-500/30 text-emerald-400">CUTOVER DECISION LAYER</Badge>
                  </CardTitle>
                  <p className="text-[10px] text-zinc-500 mt-1">Pure business revenue. Charges and taxes excluded. Apples-to-apples.</p>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="grid grid-cols-5 gap-2 text-[10px] text-zinc-500 uppercase tracking-wide px-3 pb-1">
                      <span>Metric</span><span className="text-right">Capture</span><span className="text-right">Legacy</span><span className="text-right">Diff</span><span className="text-right">Var %</span>
                    </div>
                    {comparison.revenue_comparison && Object.entries(comparison.revenue_comparison).map(([field, v]) => (
                      <div key={field} className={`grid grid-cols-5 gap-2 items-center rounded-lg px-3 py-2 border ${varianceBg(v.level)}`} data-testid={`rev-${field}`}>
                        <span className="text-xs text-zinc-300 capitalize">{field.replace(/_/g, ' ')}</span>
                        <span className="text-xs font-mono text-zinc-200 text-right">{field === 'total_orders' ? v.capture?.toLocaleString() : fmtCurrency(v.capture)}</span>
                        <span className="text-xs font-mono text-zinc-400 text-right">{field === 'total_orders' ? v.legacy?.toLocaleString() : fmtCurrency(v.legacy)}</span>
                        <span className={`text-xs font-mono text-right ${v.difference >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                          {v.difference >= 0 ? '+' : ''}{field === 'total_orders' ? v.difference?.toLocaleString() : fmtCurrency(v.difference)}
                        </span>
                        <span className={`text-xs font-semibold text-right ${varianceColor(v.level)}`}>{v.variance_pct}%</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* ═══ LAYER 2: Charges Comparison (passthrough, NOT revenue) ═══ */}
              <Card className="bg-zinc-900/60 border-zinc-800 border-l-2 border-l-amber-500">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-zinc-300 flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-amber-500" /> Charges Comparison
                    <Badge variant="outline" className="text-[8px] border-amber-500/30 text-amber-400">PASSTHROUGH</Badge>
                  </CardTitle>
                  <p className="text-[10px] text-zinc-500 mt-1">Service charge (liability), packaging (cost-linked), taxes (statutory). Not revenue.</p>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="grid grid-cols-5 gap-2 text-[10px] text-zinc-500 uppercase tracking-wide px-3 pb-1">
                      <span>Component</span><span className="text-right">Capture</span><span className="text-right">Legacy</span><span className="text-right">Diff</span><span className="text-right">Var %</span>
                    </div>
                    {comparison.charges_comparison && Object.entries(comparison.charges_comparison).map(([field, v]) => (
                      <div key={field} className={`grid grid-cols-5 gap-2 items-center rounded-lg px-3 py-2 border ${varianceBg(v.level)}`} data-testid={`charge-${field}`}>
                        <span className="text-xs text-zinc-300 capitalize">{field.replace(/_/g, ' ')}</span>
                        <span className="text-xs font-mono text-zinc-200 text-right">{fmtCurrency(v.capture)}</span>
                        <span className="text-xs font-mono text-zinc-400 text-right">{fmtCurrency(v.legacy)}</span>
                        <span className={`text-xs font-mono text-right ${v.difference >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                          {v.difference >= 0 ? '+' : ''}{fmtCurrency(v.difference)}
                        </span>
                        <span className={`text-xs font-semibold text-right ${varianceColor(v.level)}`}>{v.variance_pct}%</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* ═══ LAYER 3: Customer Spend Comparison (sanity check) ═══ */}
              <Card className="bg-zinc-900/60 border-zinc-800 border-l-2 border-l-blue-500">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-zinc-300 flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-blue-500" /> Customer Spend
                    <Badge variant="outline" className="text-[8px] border-blue-500/30 text-blue-400">SANITY CHECK</Badge>
                  </CardTitle>
                  <p className="text-[10px] text-zinc-500 mt-1">Total amount the customer paid (revenue + charges + tax). Must match exactly.</p>
                </CardHeader>
                <CardContent>
                  {comparison.customer_spend_comparison && Object.entries(comparison.customer_spend_comparison).map(([field, v]) => (
                    <div key={field} className={`grid grid-cols-5 gap-2 items-center rounded-lg px-3 py-2 border ${varianceBg(v.level)}`} data-testid={`spend-${field}`}>
                      <span className="text-xs text-zinc-300">Final Total / Total Amount</span>
                      <span className="text-xs font-mono text-zinc-200 text-right">{fmtCurrency(v.capture)}</span>
                      <span className="text-xs font-mono text-zinc-400 text-right">{fmtCurrency(v.legacy)}</span>
                      <span className={`text-xs font-mono text-right ${v.difference >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                        {v.difference >= 0 ? '+' : ''}{fmtCurrency(v.difference)}
                      </span>
                      <span className={`text-xs font-semibold text-right ${varianceColor(v.level)}`}>{v.variance_pct}%</span>
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* Normalized Channel Comparison */}
              {Object.keys(comparison.channel_comparison_normalized || {}).length > 0 && (
                <Card className="bg-zinc-900/60 border-zinc-800">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-sm text-zinc-300">Channel Comparison (Normalized)</CardTitle>
                      <Badge variant="outline" className="text-[8px] border-zinc-600 text-zinc-500">v{comparison.channel_normalization?.version}</Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <div className="grid grid-cols-5 gap-2 text-[10px] text-zinc-500 uppercase tracking-wide px-3 pb-1">
                        <span>Channel</span><span className="text-right">Capture</span><span className="text-right">Legacy</span><span className="text-right">Diff</span><span className="text-right">Var %</span>
                      </div>
                      {Object.entries(comparison.channel_comparison_normalized).map(([ch, v]) => {
                        const rules = comparison.channel_normalization?.mapping_rules?.[ch];
                        return (
                          <div key={ch}>
                            <div className={`grid grid-cols-5 gap-2 items-center rounded-lg px-3 py-2 border ${varianceBg(v.level)}`} data-testid={`norm-channel-${ch}`}>
                              <span className="text-xs text-zinc-300">{ch}</span>
                              <span className="text-xs font-mono text-zinc-200 text-right">{fmtCurrency(v.capture)}</span>
                              <span className="text-xs font-mono text-zinc-400 text-right">{fmtCurrency(v.legacy)}</span>
                              <span className={`text-xs font-mono text-right ${v.difference >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                {v.difference >= 0 ? '+' : ''}{fmtCurrency(v.difference)}
                              </span>
                              <span className={`text-xs font-semibold text-right ${varianceColor(v.level)}`}>{v.variance_pct}%</span>
                            </div>
                            {rules && (
                              <div className="text-[9px] text-zinc-600 px-3 mt-0.5">
                                {rules.capture?.length > 0 && <span>Capture: {rules.capture.join(', ')}</span>}
                                {rules.capture?.length > 0 && rules.legacy?.length > 0 && <span> | </span>}
                                {rules.legacy?.length > 0 && <span>Legacy: {rules.legacy.join(', ')}</span>}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Daily Variance Drilldown */}
              {comparison.daily_drilldown?.length > 0 && (
                <Card className="bg-zinc-900/60 border-zinc-800">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm text-zinc-300">Daily Variance Drilldown</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-1.5">
                      <div className="grid grid-cols-8 gap-1 text-[9px] text-zinc-500 uppercase tracking-wide px-2 pb-1">
                        <span>Date</span><span className="text-center">In Cap</span><span className="text-center">In Leg</span>
                        <span className="text-right">Cap Orders</span><span className="text-right">Leg Orders</span>
                        <span className="text-right">Cap Net Rev</span><span className="text-right">Leg Net Rev</span>
                        <span className="text-right">Var %</span>
                      </div>
                      {comparison.daily_drilldown.map(row => (
                        <div key={row.date} className={`grid grid-cols-8 gap-1 items-center rounded px-2 py-1.5 text-[11px] border ${
                          row.in_capture && row.in_legacy ? (
                            row.variance?.net_revenue?.level === 'GREEN' ? 'border-emerald-500/20 bg-emerald-500/3' :
                            row.variance?.net_revenue?.level === 'AMBER' ? 'border-amber-500/20 bg-amber-500/3' :
                            'border-red-500/20 bg-red-500/3'
                          ) : 'border-zinc-800 bg-zinc-900/40'
                        }`} data-testid={`daily-${row.date}`}>
                          <span className="text-zinc-300 font-mono">{row.date}</span>
                          <span className="text-center">{row.in_capture ? <Check className="w-3 h-3 text-blue-400 mx-auto" /> : <X className="w-3 h-3 text-zinc-600 mx-auto" />}</span>
                          <span className="text-center">{row.in_legacy ? <Check className="w-3 h-3 text-zinc-400 mx-auto" /> : <X className="w-3 h-3 text-zinc-600 mx-auto" />}</span>
                          <span className="text-right font-mono text-blue-400">{row.capture.orders}</span>
                          <span className="text-right font-mono text-zinc-400">{row.legacy.orders}</span>
                          <span className="text-right font-mono text-blue-400">{fmtCurrency(row.capture.net_sales || row.capture.net_revenue || 0)}</span>
                          <span className="text-right font-mono text-zinc-400">{fmtCurrency(row.legacy.net_revenue || row.legacy.net_sales || 0)}</span>
                          <span className={`text-right font-semibold ${
                            !row.in_capture || !row.in_legacy ? 'text-zinc-600' : varianceColor(row.variance?.net_revenue?.level || 'RED')
                          }`}>
                            {row.in_capture && row.in_legacy ? `${row.variance?.net_revenue?.variance_pct}%` : '—'}
                          </span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Discount Diagnostics */}
              {comparison.discount_diagnostics?.likely_cause && (
                <Card className="bg-zinc-900/60 border-zinc-800">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm text-zinc-300">Discount Variance Diagnosis</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className={`px-3 py-2.5 rounded-lg border text-xs ${
                      comparison.discount_diagnostics.severity === 'HIGH' ? 'border-red-500/30 bg-red-500/5' :
                      comparison.discount_diagnostics.severity === 'MEDIUM' ? 'border-amber-500/30 bg-amber-500/5' :
                      'border-zinc-700 bg-zinc-800/30'
                    }`} data-testid="discount-diag">
                      <p className="text-zinc-300">{comparison.discount_diagnostics.likely_cause}</p>
                      <div className="flex gap-4 mt-2 text-[10px] text-zinc-500">
                        <span>Capture: {fmtCurrency(comparison.discount_diagnostics.capture_discounts || 0)}</span>
                        <span>Legacy: {fmtCurrency(comparison.discount_diagnostics.legacy_discounts || 0)}</span>
                      </div>
                      {comparison.discount_diagnostics.recommendation && (
                        <p className="text-[10px] text-zinc-500 mt-1.5 italic">{comparison.discount_diagnostics.recommendation}</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Tax Diagnostics */}
              {comparison.tax_diagnostics?.likely_cause && (
                <Card className="bg-zinc-900/60 border-zinc-800">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm text-zinc-300">Tax Variance Diagnosis</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className={`px-3 py-2.5 rounded-lg border text-xs ${
                      comparison.tax_diagnostics.severity === 'HIGH' ? 'border-red-500/30 bg-red-500/5' :
                      comparison.tax_diagnostics.severity === 'MEDIUM' ? 'border-amber-500/30 bg-amber-500/5' :
                      'border-zinc-700 bg-zinc-800/30'
                    }`} data-testid="tax-diag">
                      <p className="text-zinc-300">{comparison.tax_diagnostics.likely_cause}</p>
                      <div className="flex gap-4 mt-2 text-[10px] text-zinc-500">
                        <span>Capture: {fmtCurrency(comparison.tax_diagnostics.capture_total_tax || 0)}</span>
                        <span>Legacy: {fmtCurrency(comparison.tax_diagnostics.legacy_total_tax || 0)}</span>
                        <span>Diff: {fmtCurrency(Math.abs(comparison.tax_diagnostics.difference || 0))}</span>
                      </div>
                      {/* Component comparison */}
                      {comparison.tax_diagnostics.component_comparison && Object.keys(comparison.tax_diagnostics.component_comparison).length > 0 && (
                        <div className="mt-2 space-y-1">
                          <p className="text-[9px] text-zinc-500 uppercase">Component Detail</p>
                          {Object.entries(comparison.tax_diagnostics.component_comparison).map(([comp, d]) => (
                            <div key={comp} className="flex justify-between text-[10px] bg-zinc-800/40 rounded px-2 py-1">
                              <span className="text-zinc-400">{comp}</span>
                              <span className="text-zinc-500">
                                Cap tax: {fmtCurrency(d.capture_tax_amount)} ({d.capture_rate}%)
                                {d.legacy_taxable_base > 0 && <> | Leg base: {fmtCurrency(d.legacy_taxable_base)}</>}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </div>
      )}

      {/* ─── Migration Operations Panel ─── */}
      {view === 'operations' && (
        <div className="space-y-5" data-testid="operations-panel">
          {loadingOps && !opsData && (
            <div className="text-center text-zinc-500 py-12">Loading operations summary...</div>
          )}
          {opsData && (() => {
            const s3 = opsData.stage3_readiness || {};
            const rh = opsData.reporting_health || {};
            const w24 = opsData.window_24h || {};
            const w7 = opsData.window_7d || {};
            const statusColors = { READY: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/30', NOT_READY: 'text-amber-400 bg-amber-500/10 border-amber-500/30' };
            const healthColors = { HEALTHY: 'text-emerald-400', DEGRADED: 'text-amber-400', ERROR: 'text-red-400' };
            const actColors = { QUIET: 'text-emerald-400', ACTIVE: 'text-amber-400', NEEDS_INVESTIGATION: 'text-red-400' };
            const sevColors = { HIGH: 'text-red-400', MEDIUM: 'text-amber-400', LOW: 'text-zinc-500' };

            return (
              <>
                {/* Stage 3 Readiness Card */}
                <Card className={`border ${statusColors[s3.status] || 'border-zinc-700'} bg-zinc-900/60`} data-testid="stage3-readiness">
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-sm font-semibold text-zinc-100">Stage 3 Readiness</h3>
                      <Badge variant="outline" className={`text-xs ${statusColors[s3.status] || ''}`}>{s3.status}</Badge>
                    </div>
                    <div className="grid grid-cols-3 gap-4 text-xs">
                      <div>
                        <p className="text-zinc-500 mb-1">Legacy Activity</p>
                        <p className={`font-medium ${actColors[s3.legacy_activity] || 'text-zinc-300'}`}>{(s3.legacy_activity || '').replace('_', ' ')}</p>
                      </div>
                      <div>
                        <p className="text-zinc-500 mb-1">Reporting Health</p>
                        <p className={`font-medium ${healthColors[s3.reporting_health] || 'text-zinc-300'}`}>{s3.reporting_health}</p>
                      </div>
                      <div>
                        <p className="text-zinc-500 mb-1">Migration Stage</p>
                        <p className="font-medium text-zinc-300">{opsData.migration_stage}</p>
                      </div>
                    </div>
                    {s3.reasons?.length > 0 && (
                      <div className="mt-3 text-[10px] text-amber-300/70 bg-amber-500/5 rounded p-2">
                        {s3.reasons.map((r, i) => <p key={i}>{r}</p>)}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Blocked Legacy Attempts */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[{ label: 'Last 24 Hours', data: w24 }, { label: 'Last 7 Days', data: w7 }].map(({ label, data }) => (
                    <Card key={label} className="bg-zinc-900/60 border-zinc-800" data-testid={`window-${label.includes('24') ? '24h' : '7d'}`}>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="text-xs font-semibold text-zinc-300">{label}</h4>
                          <span className={`text-lg font-bold ${data.blocked_attempts > 0 ? 'text-amber-400' : 'text-emerald-400'}`}>
                            {data.blocked_attempts}
                          </span>
                        </div>
                        <p className="text-[10px] text-zinc-500 mb-3">{data.unique_callers} caller{data.unique_callers !== 1 ? 's' : ''} across {data.unique_endpoints} endpoint{data.unique_endpoints !== 1 ? 's' : ''}</p>

                        {/* Top Endpoints */}
                        {data.top_endpoints?.length > 0 && (
                          <div className="space-y-1.5">
                            <p className="text-[10px] text-zinc-500 font-medium uppercase tracking-wider">Top Endpoints</p>
                            {data.top_endpoints.slice(0, 4).map((ep, i) => (
                              <div key={i} className="flex items-center justify-between text-[10px] py-1 border-b border-zinc-800/50 last:border-0">
                                <div className="flex items-center gap-2 min-w-0">
                                  <span className={`font-medium ${sevColors[ep.severity] || 'text-zinc-400'}`}>{ep.severity}</span>
                                  <span className="text-zinc-400 truncate">{ep.endpoint.replace('POST ', '')}</span>
                                </div>
                                <div className="flex items-center gap-2 shrink-0">
                                  <span className="text-zinc-300 font-medium">{ep.count}x</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Top Callers */}
                        {data.top_callers?.length > 0 && (
                          <div className="mt-3 space-y-1.5">
                            <p className="text-[10px] text-zinc-500 font-medium uppercase tracking-wider">Top Callers</p>
                            {data.top_callers.slice(0, 3).map((c, i) => (
                              <div key={i} className="text-[10px] py-1 border-b border-zinc-800/50 last:border-0">
                                <div className="flex items-center justify-between">
                                  <span className="text-zinc-400 truncate max-w-[150px]">{c.caller_id?.slice(0, 12)}...</span>
                                  <div className="flex items-center gap-2">
                                    <Badge variant="outline" className={`text-[8px] ${
                                      c.caller_type === 'AUTOMATED' ? 'border-red-500/40 text-red-400' :
                                      c.caller_type === 'USER_DRIVEN' ? 'border-blue-500/40 text-blue-400' :
                                      'border-zinc-600 text-zinc-500'
                                    }`}>{c.caller_type}</Badge>
                                    <span className="text-zinc-300 font-medium">{c.count}x</span>
                                  </div>
                                </div>
                                <p className="text-[9px] text-zinc-600 mt-0.5">Last: {c.last_seen?.slice(0, 19).replace('T', ' ')}</p>
                              </div>
                            ))}
                          </div>
                        )}
                        {data.blocked_attempts === 0 && <p className="text-[10px] text-emerald-400/60 text-center py-2">No blocked attempts</p>}
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {/* Active Reporting Health */}
                <Card className="bg-zinc-900/60 border-zinc-800" data-testid="reporting-health">
                  <CardContent className="p-4">
                    <h4 className="text-xs font-semibold text-zinc-300 mb-3">Active Reporting Health</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      {[
                        { key: 'pnl', label: 'P&L', icon: 'TrendingUp' },
                        { key: 'dashboard', label: 'Dashboard', icon: 'LayoutDashboard' },
                        { key: 'forecast', label: 'Forecast', icon: 'LineChart' },
                      ].map(({ key, label }) => {
                        const h = rh[key] || {};
                        return (
                          <div key={key} className="bg-zinc-800/40 rounded-lg p-3" data-testid={`health-${key}`}>
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-[11px] font-medium text-zinc-300">{label}</span>
                              <span className={`text-[10px] font-semibold ${healthColors[h.status] || 'text-zinc-500'}`}>{h.status}</span>
                            </div>
                            <div className="space-y-1 text-[10px]">
                              {h.source && <div className="flex justify-between"><span className="text-zinc-500">Source</span><span className="text-zinc-300">{h.source}</span></div>}
                              {h.fallback_used !== undefined && <div className="flex justify-between"><span className="text-zinc-500">Fallback</span><span className={h.fallback_used ? 'text-amber-400' : 'text-emerald-400'}>{h.fallback_used ? 'YES' : 'None'}</span></div>}
                              {h.net_sales !== undefined && <div className="flex justify-between"><span className="text-zinc-500">Net Sales</span><span className="text-zinc-300">{Number(h.net_sales).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</span></div>}
                              {h.confidence !== undefined && h.confidence !== null && <div className="flex justify-between"><span className="text-zinc-500">Confidence</span><span className="text-zinc-300">{h.confidence}%</span></div>}
                              {h.forecast_status && <div className="flex justify-between"><span className="text-zinc-500">Status</span><span className="text-zinc-300">{h.forecast_status}</span></div>}
                              {h.snapshots !== undefined && <div className="flex justify-between"><span className="text-zinc-500">Snapshots</span><span className="text-zinc-300">{h.snapshots}</span></div>}
                              {h.error && <p className="text-red-400 mt-1">{h.error}</p>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>

                {/* Refresh */}
                <div className="flex justify-end">
                  <Button variant="outline" size="sm" className="border-zinc-700 text-zinc-400 text-xs"
                    onClick={loadOperationsSummary} disabled={loadingOps} data-testid="ops-refresh">
                    {loadingOps ? 'Refreshing...' : 'Refresh Summary'}
                  </Button>
                </div>
              </>
            );
          })()}
        </div>
      )}

    </div>
  );
}
